import { useState, useEffect, useMemo } from "react";
import { Box, Flex } from "@radix-ui/themes";
import { formatTimeDifference } from "../lib/helpers";

interface SystemTimeCircleTooltipProps {
  color: string;
  systemTime?: string;
  fetchedAt?: Date;
}

export const SystemTimeCircleTooltip = ({
  color,
  systemTime,
  fetchedAt,
}: SystemTimeCircleTooltipProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formattedElapsedTime = useMemo(() => {
    if (!fetchedAt) return "-";
    return formatTimeDifference(currentTime.getTime() - fetchedAt.getTime());
  }, [fetchedAt, currentTime]);

  return (
    <div className="relative">
      <Flex height="100%" width="100%" align="center" justify="center">
        <div
          className="cursor-pointer relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box
            className="system-time-circle"
            style={{ backgroundColor: color }}
          />
          <div className="absolute inset-0 -m-2 -mb-2" />
        </div>
      </Flex>

      {isHovered && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 bg-black text-white px-3 py-2 rounded text-xs z-[1000] mt-2 min-w-[160px] shadow-lg">
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black" />
          <div className="flex flex-col space-y-1 text-center">
            {systemTime && (
              <div>
                <div className="leading-tight font-semibold text-gray-300">
                  AI system time:{" "}
                  <span className="font-mono text-white">{systemTime}</span>
                </div>
              </div>
            )}
            {fetchedAt && (
              <div className="pt-1 border-t border-gray-600">
                <div className="leading-tight font-semibold text-gray-300">
                  Last updated:{" "}
                  <span className="font-mono text-white">
                    {formattedElapsedTime} ago
                  </span>
                </div>
              </div>
            )}
            <div className="pt-1 border-t border-gray-600">
              <div className="leading-tight text-gray-300">
                Updated every 30s
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
